/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { OptionType } from 'client-code/onepass';
import { OnePassAndRecordingsUserSettings } from 'client-code/user-settings';

import { InteractiveComponents } from '../../../../core/enums';
import { NdvrInteractor } from '../../../../core/interactors';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../../../interactivelayer/interactiveLayer.reducers';
import { showUserPreferencesScreen } from '../../../screens/screens.reducers';
import {
    setGhostIndexOnePassAndRecordingOptionsAction,
    setOnePassAndRecordingErrorAction,
    setOnePassAndRecordingOptionsAction,
    setSelectedIndexOnePassAndRecordingOptionsAction,
} from './onepassandrecordings.actions';
import { getVisibleOptionsCount } from './onepassandrecordings.reducers';

const optionsOrder = [
    'include',
    OptionType.StartFrom,
    OptionType.RentOrBuy,
    OptionType.RecordType,
    OptionType.VideoQuality,
    OptionType.KeepAtMost,
    OptionType.KeepUntil,
    OptionType.AlwaysShowOverlay,
];

export const onKeyBackOnepassAndRecordingsOptionsMenu =
    () => async (dispatch: Function, getState: Function) => {
        dispatch(showUserPreferencesScreen());

        const { options, hasError } = getState().onePassAndRecordingOptions;
        const ndvrInteractor = new NdvrInteractor();
        try {
            await ndvrInteractor.setOnePassAndRecordingsUserSettings(options?.settingsValues);

            if (hasError) {
                dispatch(setOnePassAndRecordingErrorAction(false));
            }
        } catch (error) {
            dispatch(setOnePassAndRecordingErrorAction(true));
        }
    };

export const onKeyUpOnePassAndRecordingsOptions =
    () => (dispatch: Function, getState: Function) => {
        if (!isPageValidForKeyHandlers(getState)) return;
        const { selectedIndex } = getState().onePassAndRecordingOptions;

        dispatch(
            setSelectedIndexOnePassAndRecordingOptionsAction(
                selectedIndex > 0 ? selectedIndex - 1 : 0,
            ),
        );
    };

export const onKeyDownOnePassAndRecordingsOptions =
    () => (dispatch: Function, getState: Function) => {
        if (!isPageValidForKeyHandlers(getState)) return;
        const { selectedIndex, options } = getState().onePassAndRecordingOptions;

        const visibleOptionsCount = getVisibleOptionsCount(options);
        dispatch(
            setSelectedIndexOnePassAndRecordingOptionsAction(
                selectedIndex < visibleOptionsCount - 1
                    ? selectedIndex + 1
                    : visibleOptionsCount - 1,
            ),
        );
    };

export const onKeyHorizontalOnePassAndRecordingsOptions = (
    dispatch: Function,
    getState: Function,
    isShiftLeft: boolean,
) => {
    if (!isPageValidForKeyHandlers(getState)) return;
    const { selectedIndex, options } = getState().onePassAndRecordingOptions;

    const selectedOption: string = optionsOrder[selectedIndex];

    options.settingsValues[selectedOption] = getNewSelectedProperty(
        options,
        selectedOption,
        isShiftLeft,
    );
    dispatch(setOnePassAndRecordingOptionsAction(options));
};

export const onKeyLeftOnePassAndRecordingsOptions =
    () => (dispatch: Function, getState: Function) => {
        onKeyHorizontalOnePassAndRecordingsOptions(dispatch, getState, true);
    };

export const onKeyRightOnePassAndRecordingsOptions =
    () => (dispatch: Function, getState: Function) => {
        onKeyHorizontalOnePassAndRecordingsOptions(dispatch, getState, false);
    };

const getNewSelectedProperty = (
    options: OnePassAndRecordingsUserSettings,
    selectedOption: string,
    isShiftLeft: boolean,
) => {
    const selectedOptionProperty = options.settingsValues![selectedOption];

    const selectedOptionPossibleChoices = options?.settingsOptions![selectedOption].optionList;

    const selectedOptionPropertyIndex = selectedOptionPossibleChoices.findIndex((element) => {
        const { label, ...property } = element;
        const propertyValue = Object.values(property)[0];

        if (propertyValue !== null && typeof propertyValue === 'object') {
            return JSON.stringify(propertyValue) === JSON.stringify(selectedOptionProperty);
        }

        return propertyValue === selectedOptionProperty;
    });

    const shiftingOperation = isShiftLeft
        ? selectedOptionPropertyIndex - 1 + selectedOptionPossibleChoices.length
        : selectedOptionPropertyIndex + 1;

    const { label, ...property } =
        selectedOptionPossibleChoices[shiftingOperation % selectedOptionPossibleChoices.length];

    return Object.values(property)[0];
};

export const onMouseOverOnePassAndRecordingsOptions =
    () => (dispatch: Function, getState: Function) => {
        if (!isPageValidForKeyHandlers(getState)) return;
        const { value } = getState().inputFactory;
        const { selectedIndex } = getState().onePassAndRecordingOptions;

        const position = Number(value.split('-')[0]);
        const newIndex = Number.isNaN(position) ? selectedIndex : position;

        if (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) {
            dispatch(setActiveInteractiveComponent(value));
            dispatch(setGhostIndexOnePassAndRecordingOptionsAction(true));
        } else {
            dispatch(setGhostIndexOnePassAndRecordingOptionsAction(false));
        }

        dispatch(setSelectedIndexOnePassAndRecordingOptionsAction(newIndex));
    };

export const onMouseOutOnePassAndRecordingsOptions =
    () => (dispatch: Function, getState: Function) => {
        if (!isPageValidForKeyHandlers(getState)) return;
        const { value } = getState().inputFactory;

        if (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) {
            dispatch(clearActiveInteractiveComponent());
            dispatch(setGhostIndexOnePassAndRecordingOptionsAction(true));
        }
    };

export const onMouseClickOnePassAndRecordingOptions =
    () => (dispatch: Function, getState: Function) => {
        if (!isPageValidForKeyHandlers(getState)) return;
        const { value } = getState().inputFactory;

        switch (value) {
            case InteractiveComponents.WEST:
                dispatch(onKeyLeftOnePassAndRecordingsOptions());
                break;
            case InteractiveComponents.EAST:
                dispatch(onKeyRightOnePassAndRecordingsOptions());
                break;
            default:
        }
    };

export const onCursorStateChangeOnePassAndRecordingsOptions =
    () => (dispatch: Function, getState: Function) => {
        const { selectedIndex } = getState().onePassAndRecordingOptions;
        dispatch(setSelectedIndexOnePassAndRecordingOptionsAction(selectedIndex || 0));
    };

const isPageValidForKeyHandlers = (getState: Function) => {
    if (getState().userPreferences.hasOnePassOrMyShowsGetApiError) return false;

    const { options } = getState().onePassAndRecordingOptions;
    if (options === undefined || options?.settingsOptions === undefined) return false;

    return true;
};
